import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { captionsUrl, fmtTime, saveProgress } from "../../services/TrainingApi";

/*
  تحديث 101 — مشغّل الشروحات (مشترك بين مركز الشروحات ودرج «شاهد الشرح»):
  فيديو من خادم عصف برابط موقّع (يدعم التقديم/التأخير)، أو رابط خارجي مباشر، أو YouTube غير مدرج.
  الترجمة العربية/الإنجليزية، السرعة، شريط المقاطع بعلامات قابلة للنقر، والاستكمال من آخر موضع.
  الكتابة الوحيدة: موضع المشاهدة في جداول التدريب (لا يمس أي بيانات أعمال).
*/
const SPEEDS = [0.75, 1, 1.25, 1.5, 2];

const TrainingPlayer = forwardRef(function TrainingPlayer({ video, lang = "ar", startAt = null, autoPlay = false, compact = false, admin = false, onTime }, ref) {
  const el = useRef(null);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(video?.durationSeconds || 0);
  const [speed, setSpeed] = useState(1);
  const [cc, setCc] = useState(lang === "en" ? "en" : "ar");
  const [tracks, setTracks] = useState({});
  const [resumed, setResumed] = useState(null);
  const [ytStart, setYtStart] = useState(Math.floor(startAt || 0));
  const [ytKey, setYtKey] = useState(0);
  const started = useRef(false);
  const lastSave = useRef(0);
  const posRef = useRef(0);
  const kind = video?.media?.kind;
  const id = video?.id;
  const chapters = video?.chapters || [];

  // ترجمة الدرس ← روابط محلية
  useEffect(() => {
    let alive = true;
    const urls = {};
    (async () => {
      for (const l of ["ar", "en"]) {
        if (!video?.captions?.[l]) continue;
        try { const u = await captionsUrl(id, l, admin); if (u) urls[l] = u; } catch { /* بلا ترجمة */ }
      }
      if (alive) setTracks({ ...urls });
    })();
    return () => { alive = false; Object.values(urls).forEach((u) => URL.revokeObjectURL(u)); };
  }, [id, video?.captions, admin]);

  // إظهار الترجمة المختارة فقط
  useEffect(() => {
    const v = el.current;
    if (!v) return;
    const apply = () => { for (const t of Array.from(v.textTracks || [])) t.mode = t.language === cc ? "showing" : "disabled"; };
    apply();
    const t = setTimeout(apply, 300);
    return () => clearTimeout(t);
  }, [cc, tracks]);

  const save = useCallback((extra = {}) => {
    if (!id || admin) return;
    lastSave.current = Date.now();
    saveProgress(id, { position: Math.round((posRef.current || 0) * 100) / 100, ...extra }).catch(() => {});
  }, [id, admin]);

  // حفظ عند مغادرة الدرس
  useEffect(() => () => { if (started.current) save(); }, [save]);

  useImperativeHandle(ref, () => ({
    seek(t, play = true) {
      const v = el.current;
      if (kind === "youtube") {
        setYtStart(Math.floor(t)); setYtKey((k) => k + 1); posRef.current = t;
        if (!started.current) { started.current = true; save({ started: true }); } else save();
        return;
      }
      if (!v) return;
      v.currentTime = Math.max(0, t);
      setResumed(null);
      if (play) v.play().catch(() => {});
    },
    currentTime: () => posRef.current,
  }), [kind, save]);

  const onLoaded = () => {
    const v = el.current;
    if (!v) return;
    if (v.duration && Number.isFinite(v.duration)) setDuration(v.duration);
    const p = video?.progress;
    if (startAt != null && startAt > 0) v.currentTime = startAt;
    else if (p && !p.completed && Number(p.position) > 5 && (!v.duration || Number(p.position) < v.duration - 3)) {
      v.currentTime = Number(p.position);
      setResumed(Number(p.position));
    }
    v.playbackRate = speed;
    if (autoPlay) v.play().catch(() => {});
  };

  const onTimeUpdate = () => {
    const v = el.current;
    if (!v) return;
    posRef.current = v.currentTime;
    setTime(v.currentTime);
    onTime?.(v.currentTime);
    if (started.current && !v.paused && Date.now() - lastSave.current > 10000) save();
  };

  const onPlay = () => {
    if (!started.current) { started.current = true; save({ started: true }); }
  };

  const setRate = (r) => { setSpeed(r); if (el.current) el.current.playbackRate = r; };
  const total = duration || video?.durationSeconds || 0;

  if (!video) return null;
  return (
    <div className={`trp${compact ? " trp--compact" : ""}`} data-testid="training-player" data-no-translate>
      <div className="trp__screen">
        {kind === "youtube" ? (
          <iframe
            key={ytKey}
            title={video.titleAr}
            src={`https://www.youtube-nocookie.com/embed/${video.media.url}?start=${ytStart}&rel=0&modestbranding=1&cc_load_policy=1&hl=${lang}${ytKey || autoPlay ? "&autoplay=1" : ""}`}
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            data-testid="training-youtube"
          />
        ) : (
          <video
            ref={el}
            src={video.media?.url}
            poster={video.posterUrl || undefined}
            controls
            playsInline
            preload="metadata"
            onLoadedMetadata={onLoaded}
            onTimeUpdate={onTimeUpdate}
            onPlay={onPlay}
            onPause={() => started.current && save()}
            onEnded={() => save({ completed: true })}
            data-testid="training-video"
          >
            {Object.entries(tracks).map(([l, u]) => (
              <track key={l} kind="subtitles" srcLang={l} src={u} label={l === "ar" ? "العربية" : "English"} default={l === cc} />
            ))}
          </video>
        )}
      </div>

      {kind !== "youtube" && total > 0 && (
        <div className="trp__strip" aria-label="مقاطع الفيديو">
          {/* الشريط من اليسار لليمين مثل شريط المشغّل نفسه */}
          <div className="trp__bar" dir="ltr" onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            const x = e.clientX - r.left;
            if (el.current) el.current.currentTime = Math.max(0, Math.min(total, (x / r.width) * total));
          }} data-testid="training-bar">
            <span className="trp__fill" style={{ width: `${Math.min(100, (time / total) * 100)}%` }} />
            {chapters.map((c) => (
              <button key={c.id} type="button" className="trp__mark" style={{ left: `${(Number(c.start) / total) * 100}%` }}
                title={`${fmtTime(c.start)} — ${lang === "en" ? c.titleEn || c.titleAr : c.titleAr}`}
                onClick={(e) => { e.stopPropagation(); if (el.current) { el.current.currentTime = Number(c.start); el.current.play().catch(() => {}); } }} />
            ))}
          </div>
          <div className="trp__tools">
            <span className="trp__time" dir="ltr">{fmtTime(time)} / {fmtTime(total)}</span>
            {resumed != null && (
              <span className="trp__resume" data-testid="training-resumed">
                استكملت من <b dir="ltr">{fmtTime(resumed)}</b>
                <button type="button" onClick={() => { if (el.current) el.current.currentTime = 0; setResumed(null); }}>من البداية</button>
              </span>
            )}
            <span className="trp__sp" />
            {(tracks.ar || tracks.en) && (
              <label className="trp__sel" title="الترجمة">
                <span>CC</span>
                <select value={cc} onChange={(e) => setCc(e.target.value)} data-testid="training-cc">
                  {tracks.ar && <option value="ar">عربي</option>}
                  {tracks.en && <option value="en">English</option>}
                  <option value="off">إيقاف</option>
                </select>
              </label>
            )}
            <label className="trp__sel" title="السرعة">
              <select value={speed} onChange={(e) => setRate(Number(e.target.value))} data-testid="training-speed">
                {SPEEDS.map((s) => <option key={s} value={s}>×{s}</option>)}
              </select>
            </label>
          </div>
        </div>
      )}
    </div>
  );
});

export default TrainingPlayer;
